import React from "react";
import { Box, Typography, Paper, Button, Tooltip } from "@mui/material";
import { EventAvailable, EventBusy, ArrowBack } from "@mui/icons-material";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { doc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";

const DateAvailabilityView = ({
  dateStatus,
  selectedDateStr,
  unavailableDates,
  setUnavailableDates,
  handleCloseDetails,
  setSnackbar,
}) => {
  const isUnavailable = dateStatus === "unavailable";
  const displayDate = formatDateForDisplay(selectedDateStr);

  // Toggle date availability - moved inside component
  const toggleUnavailableDate = async () => {
    try {
      const dateRef = doc(db, "bus_unavailable_dates", selectedDateStr);
      const isCurrentlyUnavailable = unavailableDates.includes(selectedDateStr);

      if (isCurrentlyUnavailable) {
        // Make the date available
        await deleteDoc(dateRef);
        setUnavailableDates((prev) =>
          prev.filter((date) => date !== selectedDateStr)
        );
        setSnackbar({
          open: true,
          message: `Date marquée comme disponible`,
          severity: "success",
        });
      } else {
        // Make the date unavailable
        await setDoc(dateRef, { reason: "Marquée indisponible par l'admin" });
        setUnavailableDates((prev) => [...prev, selectedDateStr]);
        setSnackbar({
          open: true,
          message: `Date marquée comme indisponible`,
          severity: "success",
        });
      }
      handleCloseDetails();
    } catch (error) {
      console.error("Error updating date availability:", error);
      setSnackbar({
        open: true,
        message: "Erreur lors de la mise à jour de la disponibilité",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={2}
        className={
          isUnavailable ? "unavailable-date-paper" : "available-date-paper"
        }
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: isUnavailable ? "#666" : "#c84653",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isUnavailable ? (
            <>
              <EventBusy sx={{ mr: 1 }} /> Date actuellement indisponible
            </>
          ) : (
            <>
              <EventAvailable sx={{ mr: 1 }} /> Date disponible
            </>
          )}
        </Typography>

        <Typography paragraph sx={{ mb: 3 }}>
          {isUnavailable
            ? `Cette date (${displayDate}) est marquée comme indisponible et n'apparaît pas dans les options de réservation pour les clients.`
            : `Cette date (${displayDate}) est actuellement disponible pour les réservations. Les clients peuvent réserver cette date via le formulaire de réservation.`}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            mb: 3,
            color: "#666",
          }}
        >
          {isUnavailable
            ? "Vous pouvez rendre cette date disponible pour les réservations en cliquant sur le bouton ci-dessous."
            : "Vous pouvez bloquer cette date pour empêcher les réservations en cliquant sur le bouton ci-dessous."}
        </Typography>

        <Tooltip
          title={
            isUnavailable
              ? "Rendre cette date disponible pour les réservations"
              : "Bloquer cette date pour empêcher les réservations"
          }
          placement="top"
          arrow
          classes={{ tooltip: "admin-tooltip" }}
        >
          <Button
            variant="contained"
            className={
              isUnavailable
                ? "make-available-button admin-action-button"
                : "make-unavailable-button admin-action-button"
            }
            startIcon={isUnavailable ? <EventAvailable /> : <EventBusy />}
            onClick={toggleUnavailableDate}
            sx={{ mt: 2 }}
          >
            {isUnavailable
              ? "Marquer comme disponible"
              : "Marquer comme indisponible"}
          </Button>
        </Tooltip>
      </Paper>

      <Button
        variant="contained"
        className="back-button admin-action-button"
        onClick={handleCloseDetails}
        sx={{ mt: 2 }}
        startIcon={<ArrowBack />}
      >
        Retour
      </Button>
    </Box>
  );
};

export default DateAvailabilityView;
